import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { Field, Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";

import { TranslatedText } from "@/components/common/text";
import { ClickOutside } from "@/components/common/other/ClickOutside";
import { API } from "@/services/API";

export const Feedback = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [text, setText] = useState("");
    const [topic, setTopic] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const textAreaRef = useRef(null);
    const feedbackButtonRef = useRef(null);
    const feedbackListRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const topics = ["Functionality", "Design", "Performance issue", "Other"];

    const apiTopic = (topic) => {
        return topic.toLowerCase().replace(" ", "_");
    };

    const handleSubmit = () => {
        try {
            setLoading(true);
            API.post("/feedback", {
                content: `${text} - from path: ${window.location.href}`,
                type: apiTopic(topic || topics[3]),
            })
                .then(() => {
                    setError(null);
                    setSubmitted(true);
                    setText("");
                    setTopic(null);
                    setTimeout(() => {
                        setIsOpen(false);
                    }, 3000);
                })
                .catch((e) => {
                    setSubmitted(true);
                    const errorMessage = e || "An error occurred while submitting feedback.";
                    console.log("Error catch:", errorMessage);
                    setError(errorMessage);
                })
                .finally(() => {
                    setLoading(false);
                    setTimeout(() => {
                        setSubmitted(false);
                    }, 5000);
                });
        } catch (e) {
            console.log("Request failed:", e);
        }
    };
    const handleClose = () => {
        setSubmitted(false);
        if (!isOpen) {
            setIsOpen(true);
            setTimeout(() => {
                textAreaRef.current.focus();
            }, 500);
        } else {
            setIsOpen(false);
        }
    };

    const handleClickAway = (event) => {
        if (
            (!feedbackListRef.current || !feedbackListRef.current.contains(event.target)) &&
            feedbackButtonRef.current &&
            !feedbackButtonRef.current.contains(event.target)
        ) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        if (isOpen && textAreaRef.current) textAreaRef.current.focus();
    }, [isOpen]);

    return (
        <div className="fixed bottom-5 right-[18px] flex flex-col items-end z-80">
            <ClickOutside onClick={handleClickAway}>
                <div
                    className={`box-border w-[302px] bg-cm-p-background rounded overflow-hidden transition-all duration-500 ease-in-out border border-cm-p-outline ${isOpen ? "h-[354px]" : "h-[0px] border-0"}`}
                >
                    <div
                        className={`flex flex-col px-5 py-4 w-full h-full justify-end transition-[visibility] duration-500 ease-in-out ${isOpen ? "visible" : "invisible"}`}
                    >
                        {!submitted ? (
                            <>
                                <h4 className="text-cm-p-text-primary text-[18px] leading-[28px] mb-4">
                                    <TranslatedText>Share your ideas with us!</TranslatedText>
                                </h4>
                                <textarea
                                    className="w-full min-h-[152px] h-[152px] px-4 py-3 bg-cm-p-surface-container rounded resize-none text-base border-none focus:border-none focus:shadow-none focus:ring-0 focus:ring-offset-0 text-cm-p-text-primary"
                                    name="feedback"
                                    id="feedback"
                                    placeholder="Enter your suggestions here"
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    ref={textAreaRef}
                                />
                                <Field className="flex flex-col bg-cm-p-surface-container h-12 w-full mt-2 rounded">
                                    <Listbox
                                        value={topic}
                                        onChange={(v) => {
                                            setTopic(v);
                                        }}
                                    >
                                        {({ open }) => (
                                            <>
                                                <ListboxButton className="inline-flex flex-row justify-between h-12 px-4 py-1 rounded items-center">
                                                    <span
                                                        className={`text-base ${topic ? "text-cm-p-text-primary" : "text-cm-p-text-secondary"}`}
                                                    >
                                                        {topic || <TranslatedText>Select topic</TranslatedText>}
                                                    </span>
                                                    <ChevronDown
                                                        className={`opacity-50 text-cm-p-text-primary transition ease-in ${open ? "rotate-180" : ""}`}
                                                    />
                                                </ListboxButton>
                                                <ListboxOptions
                                                    anchor="top"
                                                    portal={false}
                                                    modal={false}
                                                    className="z-90 w-[260px] [--anchor-gap:4px] py-1"
                                                >
                                                    <div ref={feedbackListRef} className="flex flex-col rounded bg-cm-p-surface-container">
                                                        {topics.map((item, index) => (
                                                            <ListboxOption
                                                                key={item}
                                                                value={item}
                                                                className="inline-flex justify-center flex-col px-4 h-12 text-cm-p-text-primary text-base leading-12 relative cursor-pointer"
                                                            >
                                                                <span>{item}</span>
                                                                {index < topics.length - 1 ? (
                                                                    <span className="w-[228px] absolute bottom-0 border-b border-cm-border" />
                                                                ) : null}
                                                            </ListboxOption>
                                                        ))}
                                                    </div>
                                                </ListboxOptions>
                                            </>
                                        )}
                                    </Listbox>
                                </Field>
                                <button
                                    className="w-full min-h-12 h-12 bg-cm-accent rounded mt-5 text-base text-cm-text-on-accent disabled:opacity-75 font-semibold"
                                    onClick={handleSubmit}
                                    disabled={!text || loading}
                                >
                                    <TranslatedText>Submit</TranslatedText>
                                </button>
                            </>
                        ) : error ? (
                            <>
                                <div className="text-cm-p-on-container-red mb-[6px] mt-[84px] w-full flex items-center justify-center">
                                    <X size={48} />
                                </div>
                                <div className="text-cm-p-on-container-red text-base text-center flex-1">
                                    <TranslatedText>
                                        Feedback submit error!
                                        <br />
                                        <br />
                                        {error}
                                    </TranslatedText>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="text-cm-accent mb-[6px] mt-[84px] w-full flex items-center justify-center">
                                    <Check size={48} />
                                </div>
                                <div className="text-cm-p-text-primary text-base text-center flex-1">
                                    <TranslatedText>
                                        Feedback submitted!
                                        <br />
                                        <br />
                                        Thank you for sharing your opinion and helping us improve.
                                    </TranslatedText>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </ClickOutside>
            <div className="mt-4">
                <button
                    className="px-[15px] h-[54px] bg-cm-p-surface-container rounded inline-flex items-center justify-center text-cm-p-text-secondary border border-cm-p-outline"
                    type="button"
                    onClick={handleClose}
                    ref={feedbackButtonRef}
                >
                    {isOpen ? <X size={24} /> : <TranslatedText className="py-[15px] px-[25px] text-base">Leave feedback</TranslatedText>}
                </button>
            </div>
        </div>
    );
};
